'use client';

import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth';
import {
  SplashFaceIdStepElementContentModel,
  SplashFaceIdStepPropertiesModel,
} from '@/services/platform/api/model';
import ModuleWrapper from '../../../module/wrapper';
import { cn, ModuleProperties, SplashContext, useClientTranslation } from '@/lib/core';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Heading } from '@/components/ui/heading';
import { parseText } from '@/lib/core/utils';

export const SplashFaceIdStep = (props: ModuleProperties) => {
  const content = props.content as SplashFaceIdStepElementContentModel | undefined;
  const properties = content?.properties as SplashFaceIdStepPropertiesModel | undefined;

  const translation = useClientTranslation();

  const ctx = props.context as SplashContext | undefined;

  const isVerifyMode = ctx?.biometricVerifyMode ?? false;
  const biometryKind = ctx?.biometryKind ?? 'face';
  const isFace = biometryKind === 'face';
  const title = isFace ? properties?.allowFaceIdTitle : properties?.allowFingerprintTitle;
  const errorText = isFace ? properties?.allowFaceIdError : properties?.allowFingerprintError;

  const [error, setError] = useState('');

  const triggerBiometric = async () => {
    setError('');
    try {
      if (Capacitor.isNativePlatform()) {
        await BiometricAuth.authenticate({
          reason: isVerifyMode
            ? translation('App.Labels.AuthenticateContinue')
            : translation('App.Labels.EnableBiometricLogin'),
          cancelTitle: translation('App.Labels.Cancel'),
          allowDeviceCredential: false,
        });
      }
      ctx?.onFaceId?.();
    } catch (e: unknown) {
      const pluginError = e instanceof Error ? e.message : String(e);
      console.error('[FaceIdStep] biometric failed:', pluginError);
      setError(errorText || pluginError || translation('App.Labels.AuthenticationFailed'));
    }
  };

  // Auto-trigger the native biometric dialog when entering verify mode
  useEffect(() => {
    if (isVerifyMode) triggerBiometric();
  }, []); // intentional: runs once on mount

  if (!content) return null;

  const description = isFace ? properties?.allowFaceIdDescription : properties?.allowFingerprintDescription;
  const enableLabel = isVerifyMode
    ? isFace
      ? properties?.enterFaceIdLabel
      : properties?.enterFingerprintLabel
    : isFace
      ? properties?.enableFaceIdLabel
      : properties?.enableFingerprintLabel;
  const skipLabel = isFace ? properties?.skipFaceIdLabel : properties?.skipFingerprintLabel;
  const fallbackLabel = isFace ? properties?.enterFaceIdFallback : properties?.enterFingerprintFallback;

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        base: cn(['step__face-id', 'justify-start!', 'min-h-[calc(100vh)] p-4 bg-white']),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex', 'flex-col', 'flex-1', 'justify-between', 'gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      <div className="flex flex-1 flex-col justify-between pt-14 pb-8">
        <div className="flex flex-col items-center gap-10.5">
          <div className="flex size-20 items-center justify-center">
            <Icon
              name={isFace ? 'FaceId' : 'Fingerprint'}
              width={80}
              height={80}
              className="text-jncquoi-neutral-1000"
            />
          </div>
          <div className="flex flex-col items-center gap-2 text-center">
            {title && (
              <Heading
                as="h1"
                className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[28px] leading-none font-semibold tracking-[0.5px]"
              >
                {parseText(title)}
              </Heading>
            )}
            {description && (
              <p className="font-pp-neue-montreal text-[16px] leading-[100%] tracking-[0.25px] text-[#444]">
                {parseText(description)}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col items-center gap-3">
          {error && (
            <p className="font-pp-neue-montreal text-center text-[12px] tracking-[0.25px] text-red-600">
              {error}
            </p>
          )}
          <Button
            variant="primary-solid"
            palette="dark"
            size="lg"
            className="w-full"
            onClick={triggerBiometric}
          >
            {enableLabel}
          </Button>
          {isVerifyMode ? (
            <Button
              variant="tertiary-link"
              palette="dark"
              size="lg"
              className="w-full"
              onClick={() => ctx?.onFaceIdFallback?.()}
            >
              {fallbackLabel}
            </Button>
          ) : (
            <Button
              variant="tertiary-link"
              palette="dark"
              size="lg"
              className="w-full"
              onClick={() => ctx?.onSkipFaceId?.()}
            >
              {skipLabel}
            </Button>
          )}
        </div>
      </div>
    </ModuleWrapper>
  );
};
